<template>
  <BaseDialog centered contentClass="share-opportunity-dialog" ref="dialog" :show-actions="true" :onClose="closeDialog">
    <template slot="title">{{ $t('share-opportunity-dialog.title') }}</template>
    <template slot="content">
      <div class="share-opportunity-dialog__content">
        <BaseSearchBar
          name="name"
          ref="customSearchBar"
          :placeholder="$t('share-opportunity-dialog.search')"
          hasDebounce
          isGrey
          @input="onSearch"
        />
        <div class="tenant-users-container">
          <div
            class="user-select-container"
            v-for="(user, index) in sortedUsers"
            :key="index"
            @click="onSelectedUser(user)"
          >
            <div>
              <FormCheckbox
                class="tenant-users__checkbox"
                :value="isSelected(user)"
                @input="onSelectedUser(user)"
                size="small"
              />
              <div class="user-container">
                <BaseAvatar :name="getFullName(user)" />
                <span :style="{ fontWeight: isCurrentUser(user) ? 'bold' : '' }">{{ getFullName(user) }}</span>
                <BaseTag v-if="isCurrentUser(user)" preset="secondary" tone="neutral">
                  {{ $t('shared.me') }}
                </BaseTag>
              </div>
            </div>
            <div v-if="notificationSentMap[user.id]" class="user-notification-sent-container">
              <span>{{ formatShortDateTime(notificationSentMap[user.id]) }}</span>
              <BaseIcon name="sent" />
            </div>
          </div>
        </div>
        <div class="share-opportunity-dialog__actions">
          <FormButton is-outlined class="share-opportunity-dialog__cancel" @click.native="closeDialog">{{
            $t('actions.cancel')
          }}</FormButton>
          <FormButton @click.native="onSubmit" type="submit" :disabled="isFormInvalid">
            <span>{{ $t('share-opportunity-dialog.share') }}</span>
          </FormButton>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FormButton from '@/components/form/FormButton.vue';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import BaseAvatar from '@/components/base/BaseAvatar';
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseSearchBar from '@/components/base/BaseSearchBarNew';
import BaseTag from '@/components/base/BaseTag.vue';
import { getFullName } from '@/util/user-util';
import { formatShortDateTime } from '@/util/date-util';
import { NotificationSlugs } from '@/util/constants/notificationSlugs';

export default {
  name: 'ShareOpportunityDialog',
  components: {
    BaseAvatar,
    BaseDialog,
    BaseSearchBar,
    FormButton,
    FormCheckbox,
    BaseIcon,
    BaseTag,
  },
  data() {
    return {
      opportunityId: null,
      search: '',
      selectedUsers: [],
    };
  },
  computed: {
    ...mapGetters('tenant', ['tenant']),
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('notificationStatuses', ['notificationStatuses']),
    sortedUsers() {
      const tenantUsers = this.tenant?.users ?? [];
      const search = this.search.trim().toLowerCase();

      // filter searched users (if any)
      const users = search
        ? tenantUsers.filter((user) => this.getFullName(user).toLowerCase().includes(search))
        : [...tenantUsers];
      return users.sort((a, b) => {
        if (this.isCurrentUser(a)) return -1;
        if (this.isCurrentUser(b)) return 1;
        return this.getFullName(a).localeCompare(this.getFullName(b));
      });
    },
    isFormInvalid() {
      return this.selectedUsers.length === 0;
    },
    notificationSentMap() {
      const map = {};
      const latestCreatedAt = {};
      for (const { recipientUserId, createdAt, status, sentAt } of this.notificationStatuses ?? []) {
        if (!latestCreatedAt[recipientUserId] || new Date(createdAt) > new Date(latestCreatedAt[recipientUserId])) {
          latestCreatedAt[recipientUserId] = createdAt;
          map[recipientUserId] = status === 'sent' ? sentAt : null;
        }
      }
      return map;
    },
  },
  methods: {
    ...mapActions('opportunity', ['shareOpportunity']),
    ...mapActions('notificationStatuses', ['getNotificationStatus']),
    getFullName,
    formatShortDateTime,
    openDialog(opportunityId) {
      this.opportunityId = opportunityId;

      this.$refs.dialog.openModal();

      if (this.opportunityId) {
        this.getNotificationStatus({
          opportunityId: this.opportunityId,
          notificationType: NotificationSlugs.OPPORTUNITY_SUMMARY,
        });
      }
    },
    closeDialog() {
      this.opportunityId = null;
      this.selectedUsers = [];
      this.$refs.customSearchBar.onClear();

      this.$refs.dialog.isModalOpen = false;
    },
    isCurrentUser(user) {
      return user.id === this.currentUser.id;
    },
    isSelected(user) {
      return this.selectedUsers.some((u) => u.id === user.id);
    },
    onSearch(query) {
      this.search = query;
    },
    onSelectedUser(user) {
      if (this.isSelected(user)) {
        this.selectedUsers = this.selectedUsers.filter((u) => u.id !== user.id);
      } else {
        this.selectedUsers = [...this.selectedUsers, user];
      }
    },
    onSubmit() {
      const recipientIds = this.selectedUsers.map((u) => u.id);
      this.shareOpportunity({ id: this.opportunityId, recipientIds })
        .then(() => {
          this.$toast.success(
            this.$tc('share-opportunity-dialog.success', recipientIds.length, { count: recipientIds.length }),
          );
        })
        .catch((error) => {
          this.$toast.error(error);
        })
        .finally(() => this.closeDialog());
    },
  },
  created() {},
};
</script>

<style lang="scss">
.base-dialog__share-opportunity-dialog {
  height: auto;
  width: 550px;
  position: relative;

  .base-dialog__header {
    padding-top: $space-lg;
  }
  .base-dialog__content {
    padding: $space-sm $space-xl $space-xl $space-xl !important;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}

.share-opportunity-dialog {
  &__content {
    display: flex;
    flex-direction: column;
    max-height: 70vh;
    gap: $space-sm;

    .tenant-users-container {
      min-height: 250px;
      max-height: 40vh;
      overflow: scroll;

      .user-select-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        padding-right: $space-md;

        > div {
          display: flex;
          align-items: center;
        }

        .user-container {
          display: flex;
          align-items: center;
          padding: $space-xs 0;
          gap: $space-sm;
          font-size: $font-size-small;
        }

        .user-notification-sent-container {
          display: flex;
          align-items: center;
          gap: $space-xs;
          font-size: $font-size-small;
          color: $color-normal-pale;
        }
      }
    }
  }

  &__actions {
    margin-top: $space-xl;
    justify-content: flex-end;
    display: flex;
  }

  &__cancel {
    margin-right: $space-sm;
  }
}
</style>
